import type { ScenarioWorkspaceItemPanel } from '../../model/types'

import React, { useCallback, useMemo } from 'react'
import {
	ChevronsUpDownIcon,
	LoaderCircleIcon,
	PenIcon,
	XIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	EngineBadge,
	getPricingCampaignEngine,
	GlobalPcBadge,
	isGlobalPricingCampaign,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'
import { useScenarioQuery } from '@/entities/scenario'

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseAdornmentButton,
	InputBaseFlexWrapper,
} from '@/shared/components/ui/input-base'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { cn } from '@/shared/lib/utils'

import { usePanelController } from '../ScenariosProvider'

export const ScenarioPricingCampaignToolbar = ({
	panel: { id, scenarioId, pricingCampaignId },
}: {
	panel: ScenarioWorkspaceItemPanel
}) => {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const scenarioQuery = useScenarioQuery(scenarioId!, {
		enabled: !!scenarioId,
	})
	const optimization_group_id = scenarioQuery.data?.optimization_group_id
	const pricingCampaignsQuery = usePricingCampaignsQuery(
		{
			optimization_group_id,
			scenario_id: scenarioId,
		},
		{
			enabled: !!optimization_group_id,
		}
	)
	const { updatePanel } = usePanelController(id)
	const [open, setOpen] = React.useState(false)
	const selectedPricingCampaign = useMemo(
		() =>
			pricingCampaignsQuery.data?.find(({ id }) => id === pricingCampaignId),
		[pricingCampaignsQuery.data, pricingCampaignId]
	)
	const engine = selectedPricingCampaign
		? getPricingCampaignEngine(selectedPricingCampaign)
		: null
	const handleOpenEditPricingCampaignModal = useCallback(() => {
		ampli.scenarioPanelPricingCampaignEditClick()
		showModal('PRICING_CAMPAIGN_MODAL', {
			isReadOnly: scenarioQuery.data?.is_main,
			pricingCampaignId: pricingCampaignId!,
			scenarioId: scenarioId!,
		})
	}, [pricingCampaignId, scenarioId, showModal])
	const handleRemovePricingCampaign = useCallback(() => {
		ampli.scenarioPanelPricingCampaignRemoveClick()
		updatePanel({
			pricingCampaignId: null,
		})
	}, [updatePanel])
	const handleSelectPricingCampaign = useCallback(
		(pricingCampaignId: string) => {
			ampli.scenarioPanelPricingCampaignSelect()
			updatePanel({
				pricingCampaignId,
			})
			setOpen(false)
		},
		[updatePanel]
	)

	return (
		<InputBase role='combobox' className='w-full'>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					data-testid='scenario-pricing-campaign-toolbar-trigger'
					className='flex w-full cursor-pointer items-center justify-between'
				>
					<InputBaseFlexWrapper
						className={cn('truncate', !pricingCampaignId && 'text-placeholder')}
					>
						{selectedPricingCampaign
							? selectedPricingCampaign.name
							: t('scenarios.select_pc.placeholder', {
									defaultValue: 'Select pricing campaign',
								})}
						<div className='mr-2 ml-auto flex items-center gap-1'>
							{engine && (
								<EngineBadge className='ml-auto' engine={engine}>
									{t(engine, { defaultValue: engine })}
								</EngineBadge>
							)}
							{isGlobalPricingCampaign(selectedPricingCampaign) && (
								<GlobalPcBadge />
							)}
						</div>
					</InputBaseFlexWrapper>
					<InputBaseAdornment>
						<ChevronsUpDownIcon className='size-4 text-muted-foreground opacity-50' />
					</InputBaseAdornment>
				</PopoverTrigger>
				<PopoverContent className='w-(--radix-popover-trigger-width) p-0'>
					<Command>
						<CommandInput
							placeholder={t('search.pc.placeholder', {
								defaultValue: 'Search pricing campaigns...',
							})}
						/>
						<Virtualized asChild>
							<CommandList>
								<CommandEmpty>
									{t('no_pcs', { defaultValue: 'No pricing campaigns found' })}
								</CommandEmpty>
								{pricingCampaignsQuery.isLoading ? (
									<div className='flex items-center justify-center py-4'>
										<LoaderCircleIcon className='size-4 animate-spin opacity-50' />
									</div>
								) : (
									<VirtualizedVirtualizer>
										<CommandGroup
											heading={t('app.local_pricing_campaigns', {
												defaultValue: 'Local pricing campaigns',
											})}
										>
											{pricingCampaignsQuery.data
												?.filter(
													(campaign) => !isGlobalPricingCampaign(campaign)
												)
												.map((campaign) => {
													const engine = getPricingCampaignEngine(campaign)

													return (
														<CommandItem
															data-value={campaign.id}
															value={campaign.name}
															key={campaign.id}
															keywords={[campaign.name]}
															onSelect={() =>
																handleSelectPricingCampaign(campaign.id)
															}
														>
															{campaign.name}
															<EngineBadge className='ml-auto' engine={engine}>
																{t(engine, { defaultValue: engine })}
															</EngineBadge>
															{isGlobalPricingCampaign(campaign) && (
																<GlobalPcBadge />
															)}
														</CommandItem>
													)
												})}
										</CommandGroup>
										<CommandGroup
											heading={t('app.global_pricing_campaigns', {
												defaultValue: 'Global pricing campaigns',
											})}
										>
											{pricingCampaignsQuery.data
												?.filter((campaign) =>
													isGlobalPricingCampaign(campaign)
												)
												?.map((campaign) => {
													const engine = getPricingCampaignEngine(campaign)

													return (
														<CommandItem
															data-value={campaign.id}
															value={campaign.name}
															key={campaign.id}
															keywords={[campaign.name]}
															onSelect={() =>
																handleSelectPricingCampaign(campaign.id)
															}
														>
															{campaign.name}
															<EngineBadge className='ml-auto' engine={engine}>
																{t(engine, { defaultValue: engine })}
															</EngineBadge>
															{isGlobalPricingCampaign(campaign) && (
																<GlobalPcBadge />
															)}
														</CommandItem>
													)
												})}
										</CommandGroup>
									</VirtualizedVirtualizer>
								)}
							</CommandList>
						</Virtualized>
					</Command>
				</PopoverContent>
			</Popover>
			<Tooltip>
				<TooltipTrigger asChild>
					<InputBaseAdornmentButton
						data-testid='scenario-pricing-campaign-toolbar-edit'
						disabled={
							!pricingCampaignId ||
							isGlobalPricingCampaign(selectedPricingCampaign)
						}
						onClick={handleOpenEditPricingCampaignModal}
					>
						<PenIcon className='size-4 text-muted-foreground' />
					</InputBaseAdornmentButton>
				</TooltipTrigger>
				<TooltipContent>
					{t('pc.edit.tooltip', {
						defaultValue: 'Edit pricing campaign',
					})}
				</TooltipContent>
			</Tooltip>
			<Tooltip>
				<TooltipTrigger asChild>
					<InputBaseAdornmentButton
						data-testid='scenario-pricing-campaign-toolbar-remove'
						disabled={!pricingCampaignId}
						onClick={handleRemovePricingCampaign}
					>
						<XIcon className='size-4 text-muted-foreground' />
					</InputBaseAdornmentButton>
				</TooltipTrigger>
				<TooltipContent>
					{t('pc.close.tooltip', {
						defaultValue: 'Close pricing campaign',
					})}
				</TooltipContent>
			</Tooltip>
		</InputBase>
	)
}
